import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BANNER_ERROR_CODES,
  DEFAULT_ERROR_MESSAGE,
  ERROR_MESSAGE_MAP,
  INLINE_ERROR_CODES,
} from '../errors/error-message-map';

const AUTH_LOGOUT_ERROR_CODES = new Set<string>([
  'ERR_SESSION_EXPIRED',
  'ERR_UNAUTHORIZED',
  'ERR_TOO_MANY_ATTEMPTS',
]);

export interface ResolvedAppError {
  code: string | null;
  message: string;
  status?: number;
  rawMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class AppErrorService {
  resolve(error: unknown): ResolvedAppError {
    const code = this.resolveCode(error);
    const rawMessage = this.extractRawMessage(error);
    const message = this.getMessage(code);
    return {
      code,
      message,
      status: this.extractStatus(error),
      rawMessage,
    };
  }

  resolveCode(error: unknown): string | null {
    return this.extractCode(error);
  }

  resolveMessage(error: unknown): string {
    const resolved = this.resolve(error);
    return resolved.message;
  }

  resolveInlineMessage(error: unknown): string {
    const errorAny = error as any;
    if (errorAny?.appErrorBannerShown) {
      return '';
    }

    if (this.shouldShowBanner(error)) {
      return '';
    }
    return this.resolveMessage(error);
  }

  shouldShowBanner(error: unknown): boolean {
    const code = this.resolveCode(error);
    const requestUrl = this.resolveRequestUrl(error);
    return this.shouldShowBannerByCode(code, requestUrl);
  }

  shouldShowBannerByCode(code: string | null, requestUrl = ''): boolean {
    if (code && INLINE_ERROR_CODES.has(code)) {
      return false;
    }

    if (code && BANNER_ERROR_CODES.has(code)) {
      return true;
    }

    // For unknown API errors, keep global visibility.
    return !String(requestUrl ?? '').includes('/export');
  }

  shouldForceLogout(error: unknown): boolean {
    const resolved = this.resolve(error);
    return this.shouldForceLogoutByCode(resolved.code, resolved.rawMessage);
  }

  shouldForceLogoutByCode(code: string | null, rawMessage = ''): boolean {
    if (code && AUTH_LOGOUT_ERROR_CODES.has(this.normalizeCode(code))) {
      return true;
    }

    const normalizedRawMessage = String(rawMessage ?? '').trim().toLowerCase();
    return (
      normalizedRawMessage === 'missing token' ||
      normalizedRawMessage === 'invalid token'
    );
  }

  resolveRequestUrl(error: unknown): string {
    const errorAny = error as any;
    return String(
      errorAny?.url
      || errorAny?.rejection?.url
      || errorAny?.error?.url
      || ''
    );
  }

  getMessage(code: string | null): string {
    if (code) {
      const normalized = this.normalizeCode(code);
      const mapped = ERROR_MESSAGE_MAP[normalized];
      if (mapped) {
        return mapped;
      }
    }

    return DEFAULT_ERROR_MESSAGE;
  }

  getKnownMessage(code: string): string {
    return this.getMessage(code);
  }

  formatStateInfo(from: number, to: number, total: number): string {
    const template = this.getKnownMessage('STATE_INFO');
    return template
      .replace('{from}', String(from))
      .replace('{to}', String(to))
      .replace('{total}', String(total));
  }

  private extractStatus(error: unknown): number | undefined {
    if (error instanceof HttpErrorResponse) {
      return error.status;
    }
    const status = (error as any)?.status;
    const asNumber = Number(status);
    return Number.isFinite(asNumber) ? asNumber : undefined;
  }

  private extractCode(error: unknown): string | null {
    const errorAny = error as any;
    const candidates = [
      errorAny?.error?.code,
      errorAny?.error?.error_code,
      errorAny?.error?.errorCode,
      errorAny?.error?.message_code,
      errorAny?.error?.messageCode,
      errorAny?.code,
      errorAny?.errorCode,
      errorAny?.messageCode,
      errorAny?.error?.error,
      errorAny?.error?.message,
      errorAny?.message,
      errorAny?.rejection?.message,
      errorAny?.rejection?.code,
      errorAny?.rejection?.errorCode,
      errorAny?.rejection?.error?.code,
      errorAny?.rejection?.error?.errorCode,
    ];

    const nestedErrors = errorAny?.error?.errors;
    if (Array.isArray(nestedErrors)) {
      candidates.push(
        nestedErrors[0]?.code,
        nestedErrors[0]?.errorCode,
        nestedErrors[0]?.message_code,
        nestedErrors[0]?.messageCode,
        nestedErrors[0]?.message
      );
    }

    for (const candidate of candidates) {
      const found = this.extractCodeFromText(candidate);
      if (found) {
        return found;
      }
    }

    return null;
  }

  private extractRawMessage(error: unknown): string {
    const errorAny = error as any;
    const candidates = [
      errorAny?.error?.message,
      errorAny?.error?.error,
      errorAny?.message,
      errorAny?.rejection?.message,
      errorAny?.error_description,
      errorAny?.error?.error_description,
    ];
    for (const candidate of candidates) {
      const text = String(candidate ?? '').trim();
      if (text) {
        return text;
      }
    }
    return '';
  }

  private extractCodeFromText(value: unknown): string | null {
    const text = String(value ?? '').trim();
    if (!text) {
      return null;
    }

    if (this.looksLikeErrorCode(text)) {
      return this.normalizeCode(text);
    }

    const match = text.match(/\b(?:ERR|STATE)_[A-Z0-9_]+\b/i);
    if (match?.[0]) {
      return this.normalizeCode(match[0]);
    }

    return null;
  }

  private looksLikeErrorCode(value: string): boolean {
    return /^(ERR|STATE)_[A-Z0-9_]+$/i.test(String(value).trim());
  }

  private normalizeCode(code: string): string {
    return String(code ?? '').trim().toUpperCase();
  }
}
